<template>
    <Page v-if="isOrganizationManager" class="whats-new-management-page">
        <template #eyebrow>
            <Eyebrow icon="pi pi-sparkles">Management</Eyebrow>
        </template>
        <template #title>
            <h1 class="page-title">What's New Management</h1>
        </template>

        <Section class="entries-section">
            <template #title>
                <div class="section-title-row">
                    <span>Entries</span>
                    <Button
                        label="New Entry"
                        icon="pi pi-plus"
                        size="small"
                        @click="openCreateDialog"
                    />
                </div>
            </template>

            <DataTable
                :value="entries"
                :loading="isLoading"
                class="entries-table"
                sortField="created_at"
                :sortOrder="-1"
            >
                <template #empty>
                    <div class="empty-state">
                        <i class="pi pi-sparkles empty-icon" />
                        <p>
                            No entries yet. Create your first What's New entry.
                        </p>
                    </div>
                </template>

                <Column field="title" header="Title" sortable />
                <Column field="created_at" header="Created" sortable>
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.created_at) }}
                    </template>
                </Column>
                <Column field="updated_at" header="Updated" sortable>
                    <template #body="slotProps">
                        {{ formatDate(slotProps.data.updated_at) }}
                    </template>
                </Column>
                <Column header="Media" style="width: 100px">
                    <template #body="slotProps">
                        <div class="media-indicators">
                            <i
                                v-if="slotProps.data.image_url"
                                class="pi pi-image"
                                title="Has image"
                            />
                            <i
                                v-if="slotProps.data.video_url"
                                class="pi pi-video"
                                title="Has video"
                            />
                            <i
                                v-if="slotProps.data.link"
                                class="pi pi-external-link"
                                title="Has link"
                            />
                        </div>
                    </template>
                </Column>
                <Column header="Actions" style="width: 120px">
                    <template #body="slotProps">
                        <div class="action-buttons">
                            <Button
                                icon="pi pi-pencil"
                                text
                                size="small"
                                @click="openEditDialog(slotProps.data)"
                                aria-label="Edit"
                            />
                            <Button
                                icon="pi pi-trash"
                                text
                                size="small"
                                severity="danger"
                                @click="confirmDelete(slotProps.data)"
                                aria-label="Delete"
                            />
                        </div>
                    </template>
                </Column>
            </DataTable>
        </Section>

        <!-- Create / Edit Dialog -->
        <Dialog
            v-model:visible="dialogVisible"
            :header="editingEntry ? 'Edit Entry' : 'New Entry'"
            :style="{ width: '640px', maxWidth: '95vw' }"
            modal
            :close-on-escape="true"
        >
            <div class="dialog-form">
                <div class="field">
                    <label for="wn-title"
                        >Title <span class="required">*</span></label
                    >
                    <InputText
                        id="wn-title"
                        v-model="form.title"
                        placeholder="Entry title"
                        class="w-full"
                        :invalid="!!formErrors.title"
                    />
                    <small v-if="formErrors.title" class="p-error">{{
                        formErrors.title
                    }}</small>
                </div>

                <div class="field">
                    <label for="wn-body"
                        >Body <span class="required">*</span></label
                    >
                    <Textarea
                        id="wn-body"
                        v-model="form.body"
                        placeholder="Entry body (markdown supported)"
                        :rows="6"
                        class="w-full"
                        :invalid="!!formErrors.body"
                    />
                    <small v-if="formErrors.body" class="p-error">{{
                        formErrors.body
                    }}</small>
                </div>

                <div class="field">
                    <label
                        >Image <span class="optional">(optional)</span></label
                    >
                    <MediaUploadField
                        v-model="form.image_url"
                        kind="image"
                        :max-bytes="5 * 1024 * 1024"
                        url-placeholder="https://example.com/image.png"
                    />
                </div>

                <div class="field">
                    <label
                        >Video
                        <span class="optional"
                            >(optional — upload a clip or paste a YouTube embed
                            URL)</span
                        ></label
                    >
                    <MediaUploadField
                        v-model="form.video_url"
                        kind="video"
                        :max-bytes="50 * 1024 * 1024"
                        url-placeholder="https://www.youtube.com/embed/..."
                    />
                </div>

                <div class="field">
                    <label for="wn-link"
                        >Link <span class="optional">(optional)</span></label
                    >
                    <InputText
                        id="wn-link"
                        v-model="form.link"
                        placeholder="https://example.com/learn-more"
                        class="w-full"
                    />
                </div>

                <div class="field">
                    <label for="wn-created-at"
                        >Date
                        <span class="optional"
                            >(optional, defaults to now)</span
                        ></label
                    >
                    <DatePicker
                        id="wn-created-at"
                        v-model="form.created_at"
                        showIcon
                        showButtonBar
                        dateFormat="yy-mm-dd"
                        placeholder="Select a date"
                        class="w-full"
                    />
                </div>
            </div>

            <template #footer>
                <Button
                    label="Cancel"
                    severity="secondary"
                    @click="closeDialog"
                />
                <Button
                    :label="editingEntry ? 'Save Changes' : 'Create'"
                    :loading="isSaving"
                    @click="submitForm"
                />
            </template>
        </Dialog>

        <!-- Delete Confirmation -->
        <ConfirmDialog />
    </Page>

    <div v-else class="access-denied">
        <i class="pi pi-lock" />
        <p>You don't have permission to manage What's New entries.</p>
    </div>
</template>

<script setup lang="ts">
import DatePicker from "primevue/datepicker";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { computed, reactive, ref, watch } from "vue";

import Eyebrow from "@/components/Eyebrow.vue";
import MediaUploadField from "@/components/MediaUploadField.vue";
import Page from "@/components/Page.vue";
import Section from "@/components/Section.vue";
import { useServices } from "@/lib/openapi";
import type { WhatsNewEntryResponse } from "@/lib/openapi/gen/types.gen";
import { useUserManager } from "@/stores/user-store";

const { currentOrganizationPermissions } = useUserManager();
const confirm = useConfirm();
const toast = useToast();

const isOrganizationManager = computed(
    () => currentOrganizationPermissions.value?.can_manage_steks ?? false
);

const api = useServices({
    opts: { throwOnError: false },
    requires: { auth: true, organization: true, stek: false },
});

// ── State ────────────────────────────────────────────────────────────────────
const entries = ref<WhatsNewEntryResponse[]>([]);
const isLoading = ref(false);
const isSaving = ref(false);
const dialogVisible = ref(false);
const editingEntry = ref<WhatsNewEntryResponse | null>(null);

const emptyForm = (): {
    title: string;
    body: string;
    image_url: string;
    video_url: string;
    link: string;
    created_at: Date | null;
} => ({
    title: "",
    body: "",
    image_url: "",
    video_url: "",
    link: "",
    created_at: null,
});

const form = reactive(emptyForm());
const formErrors = reactive<{ title?: string; body?: string }>({});

// ── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (date: Date | string) => {
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
    });
};

// Anchor a user-picked calendar date at noon UTC so timezone shifts (±11h)
// can't bump it onto an adjacent day when the backend stores it naive UTC
// and the browser later parses the naive ISO string as local time.
const calendarDateToIsoNoonUtc = (d: Date): string =>
    new Date(
        Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0)
    ).toISOString();

// ── Data Loading ─────────────────────────────────────────────────────────────
const fetchEntries = async () => {
    isLoading.value = true;
    try {
        const { response, data } =
            await api.getEntriesWhatsNewWhatsNewMgtEntriesGet({});
        if (response?.ok) {
            entries.value = data ?? [];
        } else {
            toast.add({
                severity: "error",
                summary: "Error",
                detail: "Failed to load entries.",
                life: 4000,
            });
        }
    } finally {
        isLoading.value = false;
    }
};

// ── Dialog ────────────────────────────────────────────────────────────────────
const openCreateDialog = () => {
    editingEntry.value = null;
    Object.assign(form, emptyForm());
    formErrors.title = undefined;
    formErrors.body = undefined;
    dialogVisible.value = true;
};

const openEditDialog = (entry: WhatsNewEntryResponse) => {
    editingEntry.value = entry;
    form.title = entry.title;
    form.body = entry.body;
    form.image_url = entry.image_url ?? "";
    form.video_url = entry.video_url ?? "";
    form.link = entry.link ?? "";
    form.created_at = entry.created_at ? new Date(entry.created_at) : null;
    formErrors.title = undefined;
    formErrors.body = undefined;
    dialogVisible.value = true;
};

const closeDialog = () => {
    dialogVisible.value = false;
    editingEntry.value = null;
};

// ── Validation ────────────────────────────────────────────────────────────────
const validate = (): boolean => {
    formErrors.title = undefined;
    formErrors.body = undefined;
    let valid = true;
    if (!form.title.trim()) {
        formErrors.title = "Title is required.";
        valid = false;
    }
    if (!form.body.trim()) {
        formErrors.body = "Body is required.";
        valid = false;
    }
    return valid;
};

// ── Submit ────────────────────────────────────────────────────────────────────
const submitForm = async () => {
    if (!validate()) return;

    isSaving.value = true;
    try {
        const payload = {
            title: form.title.trim(),
            body: form.body.trim(),
            image_url: form.image_url.trim() || null,
            video_url: form.video_url.trim() || null,
            link: form.link.trim() || null,
            created_at: form.created_at
                ? calendarDateToIsoNoonUtc(form.created_at)
                : null,
        };

        if (editingEntry.value) {
            const { response } =
                await api.updateEntryWhatsNewWhatsNewMgtEntriesEntryIdPut({
                    path: { entry_id: editingEntry.value.identity },
                    body: payload,
                });
            if (response?.ok) {
                toast.add({
                    severity: "success",
                    summary: "Saved",
                    detail: "Entry updated successfully.",
                    life: 3000,
                });
                closeDialog();
                await fetchEntries();
            } else {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to update entry.",
                    life: 4000,
                });
            }
        } else {
            const { response } =
                await api.createEntryWhatsNewWhatsNewMgtEntriesPost({
                    body: payload,
                });
            if (response?.ok) {
                toast.add({
                    severity: "success",
                    summary: "Created",
                    detail: "Entry created successfully.",
                    life: 3000,
                });
                closeDialog();
                await fetchEntries();
            } else {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to create entry.",
                    life: 4000,
                });
            }
        }
    } finally {
        isSaving.value = false;
    }
};

// ── Delete ────────────────────────────────────────────────────────────────────
const confirmDelete = (entry: WhatsNewEntryResponse) => {
    confirm.require({
        message: `Are you sure you want to delete "${entry.title}"? This cannot be undone.`,
        header: "Delete Entry",
        icon: "pi pi-exclamation-triangle",
        rejectLabel: "Cancel",
        acceptLabel: "Delete",
        acceptClass: "p-button-danger",
        accept: async () => {
            const { response } =
                await api.deleteEntryWhatsNewWhatsNewMgtEntriesEntryIdDelete({
                    path: { entry_id: entry.identity },
                });
            if (response?.ok) {
                toast.add({
                    severity: "success",
                    summary: "Deleted",
                    detail: "Entry deleted.",
                    life: 3000,
                });
                await fetchEntries();
            } else {
                toast.add({
                    severity: "error",
                    summary: "Error",
                    detail: "Failed to delete entry.",
                    life: 4000,
                });
            }
        },
    });
};

// ── Lifecycle ─────────────────────────────────────────────────────────────────
// On direct navigation, `currentOrganizationPermissions` is populated
// asynchronously by the user store. Watch the gate so we fetch as soon as it
// flips true (and `immediate: true` covers the case where it is already true).
watch(
    isOrganizationManager,
    async (canManage) => {
        if (canManage) {
            await fetchEntries();
        }
    },
    { immediate: true }
);
</script>

<style scoped lang="scss">
.whats-new-management-page {
    .page-title {
        margin: 0;
        font-size: 1.5rem;
        font-weight: 600;
    }
}

.section-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem;
    color: var(--text-color-secondary);

    .empty-icon {
        font-size: 2rem;
    }
}

.media-indicators {
    display: flex;
    gap: 0.5rem;
    color: var(--text-color-secondary);
}

.action-buttons {
    display: flex;
    gap: 0.25rem;
}

.access-denied {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 4rem 2rem;
    color: var(--text-color-secondary);

    i {
        font-size: 3rem;
    }
}

.dialog-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem 0;

    .field {
        display: flex;
        flex-direction: column;
        gap: 0.35rem;

        label {
            font-weight: 500;
            font-size: 0.9rem;

            .required {
                color: var(--p-red-500);
                margin-left: 0.1rem;
            }

            .optional {
                color: var(--text-color-secondary);
                font-weight: 400;
                font-size: 0.8rem;
                margin-left: 0.3rem;
            }
        }
    }
}
</style>
